/* eslint-disable @typescript-eslint/no-explicit-any -- any используется как значение параметров инвариантных типов */

import { Reaction } from '@adv-frontend/reactions';
import { DistributiveOmit, WidenLiteral } from '@direct-frontend/stdlib';

import { UnknownFormInstance } from '../form/types';
import { getDefaultValue, DefaultValueOrFactory } from '../utils/getDefaultValue';
import { BaseValidationError } from '../types';

import {
    $internals,
    BoundFieldActions,
    BoundFieldAsyncActions,
    BoundFieldSelectors,
    FieldActionsMap,
    FieldAsyncActionsMap,
    FieldSelectorsMap,
    SetValueOptions,
    SkipUnknownActions,
    SkipUnknownAsyncActions,
    SkipUnknownOrAny,
    SkipUnknownSelectors,
    SkipUnknownValidationError,
} from './types';
import { FieldValidator } from './validation';
import { createReusableReactionApi } from './instanceHelpers';
import type { CreateReusableFieldReactionApi } from './instanceHelpers';
import { FieldState, FieldStateGetters } from '../store/types';

/** Тип значения "примитивного" поля */
export type Primitive = string | number | boolean | null;

/** Базовый тип для любого "примитивного" поля */
export type BasePrimitiveField = PrimitiveField<any, any, never, any, any, any, any>;
/** Базовый тип для инстанса любого "примитивного" поля */
export type BasePrimitiveFieldInstance = PrimitiveFieldInstance<any, any, any, any, any, any, any>;

/** Примитивное поле – содержит number, string или boolean */
export interface PrimitiveField<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError = never,
    FieldContextInput = never,
    FieldContext = never,
    FieldActions = never,
    FieldSelectors = never,
    FieldAsyncActions = never,
> {
    type: 'primitive';
    defaultValue: FieldValue;
    reactions?: PrimitiveFieldReaction<never, never, never, never, never, never>[];
    validators?: PrimitiveFieldValidators<
        FieldValue,
        FieldValidationError,
        FieldContextInput,
        FieldContext,
        FieldSelectors
    >;
    context?: (input: FieldContextInput) => FieldContext;
    actions?: FieldActions;
    selectors?: FieldSelectors;
    asyncActions?: FieldAsyncActions;
}

/**
 * Инстанс "примитивного" поля без asyncActions (используется для разрыва циклической зависимости)
 * @internal
 **/
export type PrimitiveFieldInstanceWithoutAsyncActions<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError = never,
    FieldContext = never,
    FieldActions = never,
    FieldSelectors = never,
    FieldName extends string = string,
> = {
    /** Id инстанса поля */
    id: string;
    /** Тип поля */
    type: 'primitive';
    /** Название поля (может использоваться как дискриминант в юнионах) */
    name: FieldName;
    /** Полный путь до поля в форме */
    path: string[];
    /** Родительская форма поля */
    form: UnknownFormInstance;
    /** Возвращает текущее значение поля */
    getValue: () => FieldValue;
    /**
     * Обновляет текущее значение поля
     * @param value Новое значение поля
     * @param options Параметры для внутреннего использования
     **/
    setValue: (value: FieldValue, options?: SetValueOptions) => void;
    /** Возвращает текущее состояние поля */
    getState: () => FieldState<FieldValue, FieldValidationError>;
    /**
     * Геттеры отдельных частей состояния поля
     *
     * Используйте `state` в реакциях и селекторах, а `getState()` — когда нужно всё состояние целиком
     *
     * @example
     * ```typescript
     * const isActionsRunning = form.getInstance('form-id').fields.myField.state.isActionsRunning();
     * //    ^? boolean
     * ```
     */
    state: FieldStateGetters<FieldValue, FieldValidationError>;
    /** Возвращает текущее значение контекста поля */
    getContext: () => FieldContext;
    /** Запускает повторную валидацию поля */
    revalidate: () => void;
    /** Добавляет ошибку к форме */
    addValidationError: (error: DistributiveOmit<FieldValidationError, 'meta'>) => void;
    /** Добавляет ошибки к форме */
    addValidationErrors: (errors: DistributiveOmit<FieldValidationError, 'meta'>[]) => void;
    /** Экшены поля */
    actions: BoundFieldActions<FieldActions>;
    /** Селекторы поля */
    selectors: BoundFieldSelectors<FieldSelectors>;
    /** Внутренние методы, которые не доступны в публичном API */
    [$internals]: {
        /** Декларация поля */
        declaration: BasePrimitiveField;
        /** Возвращает текущее значение поля */
        useValue: () => FieldValue;
        /** Возвращает текущее состояние поля */
        useState: () => FieldState<FieldValue, FieldValidationError>;
        /** Возвращает текущее значение контекста поля */
        useContext: () => FieldContext;
        /** Выставляет состояние isTouched у поля */
        touch: () => void;
        /** Скроллит страницу к полю */
        scrollToField: () => void;
    };
};

/**
 * Инстанс "примитивного" поля – предоставляет методы и селекторы для работы с его значением и состоянием
 * @typeParam FieldValue Значение поля
 * @typeParam FieldValidationError Допустимые ошибки валидации поля
 * @typeParam FieldContext Контекст поля
 * @typeParam FieldActions Экшены поля
 * @typeParam FieldSelectors Селекторы поля
 * @typeParam FieldName Название поля внутри родительского поля или формы
 **/
export type PrimitiveFieldInstance<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError = never,
    FieldContext = never,
    FieldActions = never,
    FieldSelectors = never,
    FieldAsyncActions = never,
    FieldName extends string = string,
> = PrimitiveFieldInstanceWithoutAsyncActions<
    FieldValue,
    FieldValidationError,
    FieldContext,
    FieldActions,
    FieldSelectors,
    FieldName
> & {
    /** Асинхронные экшены поля */
    asyncActions: BoundFieldAsyncActions<FieldAsyncActions>;
};

/** Объект со всеми валидаторами "примитивного" поля */
export type PrimitiveFieldValidators<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldSelectors,
> = {
    /** Валидаторы, которые будут запущены при изменении значения поля */
    onChange?: FieldValidator<
        PrimitiveField<FieldValue, FieldValidationError, FieldContextInput, FieldContext, never, never, FieldSelectors>
    >[];
    /** Валидаторы, которые будут запущены при наступлении события `blur` для поля */
    onBlur?: FieldValidator<
        PrimitiveField<FieldValue, FieldValidationError, FieldContextInput, FieldContext, never, never, FieldSelectors>
    >[];
    /** Валидаторы, которые будут запущены при отправке формы */
    onSubmit?: FieldValidator<
        PrimitiveField<FieldValue, FieldValidationError, FieldContextInput, FieldContext, never, never, FieldSelectors>
    >[];
};

/** Реакция "примитивного" поля */
export type PrimitiveFieldReaction<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<FieldValue, FieldState<FieldValue, FieldValidationError>, FieldContext>,
    FieldSelectors extends FieldSelectorsMap<FieldValue>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        PrimitiveFieldInstanceWithoutAsyncActions<
            FieldValue,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    >,
> = Reaction<
    any,
    PrimitiveFieldInstance<
        FieldValue,
        NoInfer<FieldValidationError>,
        FieldContext,
        FieldActions,
        FieldSelectors,
        FieldAsyncActions
    >
>;

/** Параметры для создания "примитивного" поля */
type PrimitiveFieldOptions<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldActions extends FieldActionsMap<
        WidenIfNotLiteral<FieldValue>,
        FieldState<WidenIfNotLiteral<FieldValue>, SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>
    >,
    FieldSelectors extends FieldSelectorsMap<WidenIfNotLiteral<FieldValue>>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        PrimitiveFieldInstanceWithoutAsyncActions<
            WidenIfNotLiteral<FieldValue>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            FieldActions,
            FieldSelectors
        >
    >,
> = {
    /** Значение поля по умолчанию (будет использовано если начальное значение формы не задано) */
    defaultValue: DefaultValueOrFactory<FieldValue>;
    /** Список валидаторов поля */
    validators?: (
        createValidators: <PassedFieldValidationError extends BaseValidationError>(
            // Используем интерфейс с функцией, чтобы иметь возможность указать значение type-параметра руками
            validators?: PrimitiveFieldValidators<
                WidenIfNotLiteral<FieldValue>,
                PassedFieldValidationError,
                FieldContextInput,
                FieldContext,
                FieldSelectors
            >
        ) => PrimitiveFieldValidators<
            WidenIfNotLiteral<FieldValue>,
            PassedFieldValidationError,
            FieldContextInput,
            FieldContext,
            FieldSelectors
        >
    ) => PrimitiveFieldValidators<
        WidenIfNotLiteral<FieldValue>,
        SkipUnknownValidationError<FieldValidationError>,
        FieldContextInput,
        FieldContext,
        FieldSelectors
    >;
    /** Экшены поля */
    actions?: FieldActions;
    /** Селекторы поля */
    selectors?: FieldSelectors;
    /** Асинхронные экшены поля */
    asyncActions?: (
        createAsyncActions: <
            T extends FieldAsyncActionsMap<
                PrimitiveFieldInstanceWithoutAsyncActions<
                    WidenIfNotLiteral<FieldValue>,
                    SkipUnknownValidationError<FieldValidationError>,
                    SkipUnknownOrAny<FieldContext>,
                    FieldActions,
                    FieldSelectors
                >
            >,
        >(
            asyncActions: T
        ) => T
    ) => FieldAsyncActions;

    /** Функция, наполняющая контекст поля */
    context?: (input: FieldContextInput) => FieldContext;

    /** Реакции поля */
    reactions?: (
        createReactions: (
            reactions: PrimitiveFieldReaction<
                WidenIfNotLiteral<FieldValue>,
                SkipUnknownValidationError<FieldValidationError>,
                SkipUnknownOrAny<FieldContext>,
                FieldActions,
                FieldSelectors,
                FieldAsyncActions
            >[]
        ) => PrimitiveFieldReaction<
            WidenIfNotLiteral<FieldValue>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            FieldActions,
            FieldSelectors,
            FieldAsyncActions
        >[],
        createReusableReaction: CreateReusableFieldReactionApi<
            PrimitiveField<
                WidenIfNotLiteral<FieldValue>,
                SkipUnknownValidationError<FieldValidationError>,
                FieldContextInput,
                SkipUnknownOrAny<FieldContext>,
                SkipUnknownActions<
                    WidenIfNotLiteral<FieldValue>,
                    FieldState<WidenIfNotLiteral<FieldValue>, SkipUnknownValidationError<FieldValidationError>>,
                    SkipUnknownOrAny<FieldContext>,
                    NoInfer<FieldActions>
                >,
                SkipUnknownSelectors<WidenIfNotLiteral<FieldValue>, NoInfer<FieldSelectors>>,
                SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
            >,
            PrimitiveFieldReaction<
                WidenIfNotLiteral<FieldValue>,
                SkipUnknownValidationError<FieldValidationError>,
                SkipUnknownOrAny<FieldContext>,
                FieldActions,
                FieldSelectors,
                FieldAsyncActions
            >
        >
    ) => PrimitiveFieldReaction<
        WidenIfNotLiteral<FieldValue>,
        SkipUnknownValidationError<FieldValidationError>,
        SkipUnknownOrAny<FieldContext>,
        FieldActions,
        FieldSelectors,
        FieldAsyncActions
    >[];
};

/** Тип значения, возвращаемого функцией `nullable(value)` */
type Nullable<T extends Primitive> = T & { __nullable: true };

/** Создает значение, в которое можно присвоить null */
export function nullable<T extends Primitive>(value: T | null): T | null {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Создаем branded-type
    return value as T | null;
}

/** Дополняет тип null'ом, если значение передано через `nullable(value)` */
type WidenWithNull<FieldValue extends Primitive> = FieldValue extends Nullable<infer Value> ? Value | null : FieldValue;

/** Тип значения, возвращаемого функцией `literal(value)` */
type Literal<T extends Primitive> = T & { __literal: true };

/** Создает литеральное значение, описывающее тип примитивного поля */
export function literal<T extends Primitive>(value: T): Literal<T> {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Создаем branded-type
    return value as Literal<T>;
}

/** Проверяет создано ли значение через функцию `literal(value)` и если нет, то расширяет его тип до базового типа */
export type WidenIfNotLiteral<FieldValue extends Primitive> =
    FieldValue extends Literal<infer LiteralValue> ? LiteralValue : WidenWithNull<WidenLiteral<FieldValue>>;

/** Создает "примитивное" поле */
export function createPrimitiveField<
    FieldValue extends Primitive,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldActions extends FieldActionsMap<
        WidenIfNotLiteral<FieldValue>,
        FieldState<WidenIfNotLiteral<FieldValue>, SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>
    >,
    FieldSelectors extends FieldSelectorsMap<WidenIfNotLiteral<FieldValue>>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        PrimitiveFieldInstanceWithoutAsyncActions<
            WidenIfNotLiteral<FieldValue>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            FieldActions,
            FieldSelectors
        >
    >,
>({
    actions,
    asyncActions,
    selectors,
    context,
    defaultValue,
    validators,
    reactions,
}: PrimitiveFieldOptions<
    FieldValue,
    FieldValidationError,
    SkipUnknownOrAny<FieldContextInput>,
    SkipUnknownOrAny<FieldContext>,
    FieldActions,
    FieldSelectors,
    FieldAsyncActions
>): PrimitiveField<
    WidenIfNotLiteral<FieldValue>,
    SkipUnknownValidationError<FieldValidationError>,
    SkipUnknownOrAny<FieldContextInput>,
    SkipUnknownOrAny<FieldContext>,
    SkipUnknownActions<
        WidenIfNotLiteral<FieldValue>,
        FieldState<WidenIfNotLiteral<FieldValue>, SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>,
        NoInfer<FieldActions>
    >,
    SkipUnknownSelectors<WidenIfNotLiteral<FieldValue>, NoInfer<FieldSelectors>>,
    SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
> {
    // Приводим тип к корректному, так как в возвращаемом значении параметры уточняются (удаляются any и unknown)
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return {
        type: 'primitive',
        defaultValue: getDefaultValue(defaultValue),
        context,
        actions,
        asyncActions: asyncActions?.(asyncActionsArg => asyncActionsArg),
        selectors,
        reactions: reactions?.(reaction => reaction, createReusableReactionApi()),
        validators: validators?.(value => value ?? {}),
    } as PrimitiveField<
        WidenIfNotLiteral<FieldValue>,
        SkipUnknownValidationError<FieldValidationError>,
        SkipUnknownOrAny<FieldContextInput>,
        SkipUnknownOrAny<FieldContext>,
        SkipUnknownActions<
            WidenIfNotLiteral<FieldValue>,
            FieldState<WidenIfNotLiteral<FieldValue>, SkipUnknownValidationError<FieldValidationError>>,
            SkipUnknownOrAny<FieldContext>,
            NoInfer<FieldActions>
        >,
        SkipUnknownSelectors<WidenIfNotLiteral<FieldValue>, NoInfer<FieldSelectors>>,
        SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
    >;
}
